<template>
  <div class="conexao">
    <!--
      Conta ligada: o que importa é qual é — num Google com três contas, saber
      que "está conectado" não diz se é a certa.
    -->
    <div v-if="conectada && !trocando" class="conexao__ligada">
      <span class="conexao__marca"><fa-icon icon="check-circle" /></span>
      <div class="conexao__conta">
        <strong>{{ conexao.conta || 'Conta conectada' }}</strong>
        <small>O acesso se renova sozinho enquanto a autorização valer.</small>
      </div>

      <div class="conexao__acoes">
        <CButton size="sm" variant="outline" color="secondary" :disabled="ocupado" @click="trocarDeConta">
          Trocar de conta
        </CButton>
        <CButton size="sm" variant="outline" color="danger" :disabled="ocupado" @click="desconectar">
          Desconectar
        </CButton>
      </div>
    </div>

    <template v-else>
      <!--
        A autorização volta pelo navegador para esta mesma tela, e o endereço
        dela precisa estar cadastrado como servidor da filial no Gestor. Por
        isso só existe depois de salvar: antes disso não há integração a que
        amarrar a conexão.
      -->
      <div v-if="provedores.length" class="conexao__provedores">
        <button
          v-for="provedor in provedores"
          :key="provedor.chave"
          type="button"
          class="botao-provedor"
          :disabled="trabalhando"
          @click="conectar(provedor)"
        >
          <!--
            A arte vem do backend junto com o provedor: é o conector que sabe
            de qual conta se trata, e um provedor novo entra sem build daqui.
          -->
          <img v-if="provedor.logo" class="botao-provedor__marca" :src="provedor.logo" :alt="provedor.nome" />
          <span class="botao-provedor__texto">
            {{ trabalhando && escolhido === provedor.chave ? 'Abrindo...' : provedor.botao }}
          </span>
        </button>

        <CButton v-if="trocando" size="sm" variant="ghost" color="secondary" :disabled="ocupado" @click="trocando = false">
          Cancelar
        </CButton>
      </div>

      <p v-else class="conexao__pendente">
        Este conector não tem conta para conectar.
      </p>
    </template>

    <CAlert v-if="erro" show color="danger" class="conexao__erro">{{ erro }}</CAlert>
  </div>
</template>

<script>
/*
 * Conectar uma conta de provedor pela autorização do Hub Versotech.
 *
 * O caminho passa pelo navegador: daqui sai um pedido de sessão, o usuário
 * autoriza na tela do próprio provedor e volta para esta mesma URL com a sessão
 * e o `state`. Quem conclui é a tela que hospeda este componente — ela é que
 * sobrevive ao redirecionamento; este componente, não.
 *
 * O token nunca passa por aqui: ele fica no Hub, e o que a integração guarda é
 * o identificador da conexão.
 *
 * Nada aqui conhece Google, Meta ou RD: a lista de provedores, a marca e o
 * texto do botão vêm prontos do manifesto do conector. É o que permite um
 * conector passar a aceitar outra conta sem tocar no painel.
 */
import { post } from '@/helpers/apiRequest';
import { urlSegura } from '@/helpers/urlSegura';

export default {
  name: 'ConexaoHub',
  props: {
    integracaoId: { type: [Number, String], default: null },
    // Estado vindo do backend: { campo, provedores, conectada, conta }.
    conexao: { type: Object, default: () => ({}) },
    /*
     * Os provedores declarados no campo do manifesto. Valem antes de a
     * integração existir — na instalação não há instância, e portanto não há
     * o bloco de conexão que o backend monta para ela.
     */
    provedoresDoCampo: { type: Object, default: () => ({}) },
    // Chave de um provedor para conectar assim que a tela abrir: é como a
    // instalação retoma o clique que a criou.
    conectarAoAbrir: { type: String, default: '' },
    // O salvar de quem hospeda a tela: é ele que destrava o botão quando a
    // criação pedida pelo clique falha na validação.
    salvando: { type: Boolean, default: false },
  },
  data() {
    return {
      ocupado: false,
      trocando: false,
      escolhido: null,
      erro: '',
    };
  },
  computed: {
    conectada() {
      return !!this.conexao.conectada;
    },
    trabalhando() {
      return this.ocupado || this.salvando;
    },
    provedores() {
      if (this.conexao.provedores && this.conexao.provedores.length) {
        return this.conexao.provedores;
      }

      return Object.keys(this.provedoresDoCampo).map((chave) => ({
        chave,
        ...this.provedoresDoCampo[chave],
      }));
    },
  },
  watch: {
    /*
     * O estado da conexão voltou do servidor — a ação terminou, deu certo ou
     * não. Sem isto, desconectar ligava `ocupado` e nada o desligava: os
     * botões de provedor ficavam travados e só recarregar a página devolvia a
     * possibilidade de reconectar.
     */
    conexao() {
      this.ocupado = false;
    },
  },
  mounted() {
    /*
     * Retomada do clique dado na instalação: a integração acabou de ser criada
     * e a tela reabriu na instância, com o provedor escolhido na query.
     */
    if (!this.conectarAoAbrir || !this.integracaoId || this.conectada) {
      return;
    }

    const provedor = this.provedores.find((item) => item.chave === this.conectarAoAbrir);

    if (provedor) {
      // Some da URL antes de sair do painel: enquanto a ordem estiver lá,
      // voltar pelo navegador — inclusive voltando do provedor — manda o
      // usuário para fora de novo, sem fim.
      this.$emit('retomada-cumprida');
      this.conectar(provedor);
    }
  },
  methods: {
    /*
     * A URL de retorno é a desta tela, sem a query: o que o Hub acrescenta
     * (sessão, status e state) não pode se somar a parâmetros de uma tentativa
     * anterior, senão a segunda volta traria duas sessões.
     */
    urlDeRetorno() {
      const { origin, pathname } = window.location;

      return origin + pathname;
    },

    /*
     * Com um provedor só não há o que escolher — trocar de conta é reautorizar
     * na mesma marca. Com vários, a lista volta a aparecer.
     */
    trocarDeConta() {
      if (this.provedores.length === 1) {
        this.conectar(this.provedores[0]);

        return;
      }

      this.trocando = true;
    },

    conectar(provedor) {
      this.escolhido = provedor.chave;
      this.erro = '';

      /*
       * Sem instância não há a que amarrar a conexão — e exigir que o usuário
       * salve primeiro é mandá-lo guardar um formulário vazio para só então
       * poder fazer o que veio fazer. Quem hospeda a tela cria a integração e
       * reabre aqui, já conectando.
       */
      if (!this.integracaoId) {
        this.$emit('salvar-e-conectar', provedor.chave);

        return;
      }

      this.ocupado = true;

      post(`/admin/integracoes/${this.integracaoId}/hub/conectar`, {
        return_url: this.urlDeRetorno(),
        provedor: provedor.chave,

        // Integração criada pelo clique de conectar está desligada: autorizada
        // a conta, passa a valer. Cancelada, continua fora do ar.
        ativar_ao_concluir: !!this.conectarAoAbrir,
      })
        .then((resposta) => {
          const destino = resposta.data && resposta.data.authorize_url;

          if (!destino) {
            this.erro = 'O Hub não devolveu o endereço de autorização.';
            this.ocupado = false;

            return;
          }

          /*
           * O endereço vem de fora e manda o navegador embora: `javascript:`
           * ou um esquema qualquer no lugar dele executaria no painel, com a
           * sessão do usuário. Só autorização por HTTPS sai daqui.
           */
          if (!urlSegura(destino) || !/^https:/i.test(destino)) {
            this.erro = 'O endereço de autorização devolvido não é válido.';
            this.ocupado = false;

            return;
          }

          // Redirecionamento, não popup: o bloqueador de popup do navegador
          // mataria o fluxo em silêncio, e o usuário não saberia por quê.
          window.location.href = destino;
        })
        .catch((erro) => {
          this.erro = erro.message || 'Não foi possível iniciar a conexão.';
          this.ocupado = false;
        });
    },

    desconectar() {
      this.$swal({
        title: 'Desconectar a conta?',
        text: 'A integração perde o acesso até alguém conectar de novo.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Desconectar',
        cancelButtonText: 'Cancelar',
      }).then((resposta) => {
        if (!resposta.value) {
          return;
        }

        this.ocupado = true;

        this.$emit('desconectar');
      });
    },
  },
};
</script>

<style scoped>
.conexao__provedores {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

/*
 * O botão de entrar com a conta segue o desenho que os provedores pedem para
 * si: fundo claro, borda fina, a marca à esquerda e o texto centrado no espaço
 * que sobra. Não é estética — é o que faz o usuário reconhecer de quem é a
 * conta antes de clicar.
 */
.botao-provedor {
  align-items: center;
  background: #ffffff;
  border: 1px solid #dadce0;
  border-radius: 6px;
  color: #1f1f1f;
  cursor: pointer;
  display: inline-flex;
  font-size: 14px;
  font-weight: 500;
  gap: 12px;
  height: 40px;
  padding: 0 16px;
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.botao-provedor:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #c7cace;
  box-shadow: 0 1px 2px rgba(60, 64, 67, 0.2);
}

.botao-provedor:focus-visible {
  outline: 2px solid #1a73e8;
  outline-offset: 2px;
}

.botao-provedor:disabled {
  color: #80868b;
  cursor: default;
  opacity: 0.65;
}

/*
 * Altura fixa e largura livre: a marca de um provedor tanto pode ser um símbolo
 * quadrado quanto o logotipo por extenso, e fixar os dois lados esmagaria o
 * segundo caso.
 */
.botao-provedor__marca {
  display: block;
  height: 18px;
  max-width: 72px;
  width: auto;
}

.botao-provedor__texto {
  line-height: 1;
}

.conexao__ligada {
  align-items: center;
  background: #f2f8f4;
  border: 1px solid #c9e7d5;
  border-radius: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 14px;
}

.conexao__marca {
  color: #1a8a4a;
  font-size: 18px;
}

.conexao__conta {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 180px;
}

.conexao__conta strong {
  color: #1d2939;
  font-size: 13px;
}

.conexao__conta small {
  color: #667085;
  font-size: 11.5px;
}

.conexao__acoes {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.conexao__pendente {
  color: #667085;
  font-size: 12px;
  margin: 0;
}

.conexao__erro {
  margin: 10px 0 0;
}
</style>
